import { onMounted, onUnmounted, nextTick, ref, unref, type Ref } from "vue";
import emitter from "tiny-emitter/instance";
import {
    collectErrors,
    getFirstElementHavingError,
    groupErrorsByProperty,
    scrollToElement,
} from "@/tabs/Operation/utilsTs";
import { AxiosError } from "axios";
import { openDialog } from "@/utils";
import { getErrorMessage } from "@/api";
import { KFormExposeApi } from "@/components/forms/Form/types";
import { formRegistry } from "@/components/forms/formRegistry";

type ParsedErrors<T = string[]> = {
    [key: string]: T;
};

type ParsedErrorResult = {
    fieldErrors: ParsedErrors<string[] | string>;
    nonFieldErrors: ParsedErrors;
    raw: AxiosError | undefined;
};

export const useKForm = ({ id }: { id?: string | Ref<string | undefined> } = {}) => {
    const formData = ref<Record<string, any> | undefined>({});
    const formApi = ref<KFormExposeApi | undefined>();

    const inputErrors = ref({});
    const formErrors = ref<string[]>([]);

    const onFormUpdate = ({ formData: newFormData }: { formData: any }) => {
        formData.value = newFormData;
    };

    const parseErrorResponse = (e: AxiosError | undefined): ParsedErrorResult | undefined => {
        if (!e?.response) {
            return;
        }

        // fånga upp icke 400 fel först
        if (e.response?.status !== 400) {
            openDialog(getErrorMessage(e), "error");
            return;
        }

        const responseData = e.response?.data;
        let nonFieldErrors: any = {};

        if (responseData?.non_field_errors) {
            const keyValueObj = groupErrorsByProperty(responseData.non_field_errors);
            // gör strängvärden till array
            nonFieldErrors = Object.fromEntries(
                Object.entries(keyValueObj).map(([key, value]) => [key, Array.isArray(value) ? value : [value]])
            );
        }
        const result = collectErrors(responseData);
        const { non_field_errors, ...fieldErrors } = result; // non_field_errors inkluderas inte här

        return {
            fieldErrors,
            nonFieldErrors,
            raw: e,
        };
    };

    const setErrors = (errors: ParsedErrorResult, unhandledErrorsAsDialog: boolean = false) => {
        inputErrors.value = errors?.fieldErrors;
        if (errors?.nonFieldErrors) {
            const values = Object.values(errors.nonFieldErrors).flat();
            formErrors.value = values;
        }
        // scrolla till första error element
        nextTick(() => {
            const firstElementWithError = getFirstElementHavingError();

            // Om det saknas element med valideringsfel och vi ändå har fieldErrors
            if (!firstElementWithError) {
                // visa som popup
                if (unhandledErrorsAsDialog) {
                    const errorOptions = {
                        description: "",
                        errorList: Object.values(errors?.fieldErrors).flat(),
                    };
                    openDialog(errorOptions, "error");
                } else {
                    // hantera dessa field errors som formErrors (array)
                    formErrors.value = Object.values(errors?.fieldErrors).flat();
                    scrollToElement(firstElementWithError);
                }
            } else {
                scrollToElement(firstElementWithError);
            }
        });
    };

    /**
     * handleErrorResponse: kör parseErrorResponse och setErrors i följd
     * @param e : AxiosError
     * @param unhandledErrorsAsDialog: fältfel som saknar matchande element i formuläret hanteras med
     *          popup (istället för att visas längst ned)
     */
    const handleErrorResponse = (e: AxiosError | undefined, unhandledErrorsAsDialog: boolean = false) => {
        const errors = parseErrorResponse(e);
        if (errors) {
            setErrors(errors, unhandledErrorsAsDialog);
        }
    };

    const onUnregisteredForm = () => {
        formData.value = undefined;
        formApi.value = undefined;
    };

    onMounted(() => {
        // id kan skickas som en ref (istället för en sträng) om värdet inte är känt vid anropstillfället,
        // läses av här, efter att children monterats.
        const currentId = unref(id);

        if (currentId) {
            formData.value = formRegistry.get(currentId)?.getFormData?.() ?? {};
            formApi.value = formRegistry.get(currentId);

            emitter.on(`form-update-${currentId}`, onFormUpdate);
        }

        emitter.on("form-unregistered", onUnregisteredForm);
    });

    onUnmounted(() => {
        emitter.off(`form-update-${unref(id)}`, onFormUpdate);
        emitter.off("form-unregistered", onUnregisteredForm);
    });

    return {
        formData,
        formApi,
        inputErrors,
        formErrors,
        parseErrorResponse,
        setErrors,
        handleErrorResponse,
    };
};
